import React from 'react';
import PropTypes from 'prop-types';

import { i18n } from 'config/locales';
import {
  HISTORY,
  HISTORY_RESIDENCE,
  HISTORY_EMPLOYMENT,
  HISTORY_EDUCATION,
  HISTORY_REVIEW,
} from 'config/formSections/history';
import { formConfig } from 'config/forms';
import { findTimelineGaps, getApplicantRequiredDuration, getThresholdYears } from 'helpers/date';

import connectSubsection from 'components/Section/shared/SubsectionConnector';

import Show from 'components/Form/Show';
import { isPVQForm } from 'helpers/form';
import ResidenceSummaryProgress from './Residence/ResidenceSummaryProgress';
import EmploymentSummaryProgress from './Employment/EmploymentSummaryProgress';
import EducationSummaryProgress from './Education/EducationSummaryProgress';
import ConnectedResidence from './Residence';
import ConnectedEmployment from './Employment';
import EducationWrapper from './Education/EducationWrapper';
import FederalWrapper from './Federal/FederalWrapper';

const sectionConfig = {
  key: HISTORY_REVIEW.key,
  section: HISTORY.name,
  store: HISTORY.store,
  subsection: HISTORY_REVIEW.name,
};

const Review = function (props) {
  const {
    applicantBirthdate,
    Education,
    Residence,
    Employment,
    formType,
    requireHistoryFederalSection,
    forPrint,
    errors,
  } = props;

  const formTypeConfig = formType && formConfig[formType];
  const residenceYears = formTypeConfig && formTypeConfig.HISTORY_RESIDENCE_YEARS;
  const employmentYears = formTypeConfig && formTypeConfig.HISTORY_EMPLOYMENT_YEARS;
  const employmentRecordYears = formTypeConfig && formTypeConfig.HISTORY_EMPLOYMENT_RECORD_YEARS;

  const residenceErrors = errors && errors[HISTORY_RESIDENCE.key];
  const employmentErrors = errors && errors[HISTORY_EMPLOYMENT.key];

  const subsectionProps = {
    required: true,
    scrollIntoView: false,
    overrideInitial: true,
  };

  const hasAttendedSchool = Education.HasAttended.value === 'Yes';
  const hasDegree = Education.HasDegree10.value === 'Yes';
  const thresholdYears = getThresholdYears(formType);

  const requiredResidence = getApplicantRequiredDuration({
    requiredDuration: {
      years: residenceYears,
    },
    applicantBirthdate,
    formType,
    type: 'residence'
  });

  const requiredEmployment = getApplicantRequiredDuration({
    requiredDuration: {
      years: employmentYears,
    },
    applicantBirthdate,
    thresholdYears,
    type: 'employment'
  });

  const validResidenceDates = Residence.List && Residence.List.items
    ? Residence.List.items
      .filter((i) => {
        if (!i.Item) { return false; }
        if (!residenceErrors || !residenceErrors.length) { return true; }
        if (residenceErrors.filter((e) => e.indexOf(i.uuid) > -1).length > 0) { return false; }
        return true;
      })
      .map((i) => i.Item.Dates)
    : [];

  const residenceGaps = findTimelineGaps(requiredResidence.coverage, validResidenceDates);

  let validEmploymentDates = Employment.List && Employment.List.items
    ? Employment.List.items
      .filter((i) => {
        if (!i.Item) { return false; }
        if (!employmentErrors || !employmentErrors.length) { return true; }
        if (employmentErrors.filter((e) => e.indexOf(i.uuid) > -1).length > 0) { return false; }
        return true;
      })
      .map((i) => i.Item.Dates)
    : [];

  if (Employment.List && Employment.List.items) {
    Employment.List.items.forEach((i) => {
      let validAdditionalEmploymentDates;
      if (i.Item && i.Item.Additional && i.Item.Additional.items) {
        validAdditionalEmploymentDates = i.Item.Additional.items
          .filter((d) => {
            if (!d.Item) { return false; }
            if (!errors || !errors.length) { return true; }
            if (!!i && errors.filter((e) => e.indexOf(i.uuid) > -1).length > 0) { return false; }
            return true;
          })
          .map((d) => d.Item.DatesEmployed);
      }
      if (validAdditionalEmploymentDates) { validEmploymentDates = validEmploymentDates.concat(validAdditionalEmploymentDates); }
    });
  }

  validEmploymentDates = validEmploymentDates.filter((i) => (
    !!i
    && i.from?.month !== ''
    && i.from?.day !== ''
    && i.from?.year !== ''
    && i.to?.month !== ''
    && i.to?.day !== ''
    && i.to?.year !== ''
  ));

  const employmentGaps = findTimelineGaps(requiredEmployment.coverage, validEmploymentDates);

  const isPvq = isPVQForm(formType);
  return isPvq ? (
    <div>
      {!forPrint && (
        <span>
          <ResidenceSummaryProgress
            years={residenceYears}
            dates={validResidenceDates}
            applicantBirthdate={applicantBirthdate}
            formType={formType}
          />

          <EmploymentSummaryProgress
            years={employmentYears}
            dates={validEmploymentDates}
            applicantBirthdate={applicantBirthdate}
            thresholdYears={thresholdYears}
            formType={formType}
          />

          <Show when={hasAttendedSchool || hasDegree}>
            <EducationSummaryProgress
              items={Education && Education.List && Education.List.items}
              errors={errors && errors[HISTORY_EDUCATION.key]}
              formType={formType}
            />
          </Show>
        </span>
      )}

      {!forPrint && <hr className="section-divider" />}

      <h1 className="section-header">{i18n.t('history.pvq.residence.collection.caption')}</h1>
      <ConnectedResidence
        {...subsectionProps}
        totalYears={requiredResidence.requiredYears}
        realtime
        gaps={residenceGaps}
        formType={formType}
      />

      <hr className="section-divider" />
      <h1 className="section-header">{i18n.t('history.pvq.education.collection.caption')}</h1>
      <EducationWrapper inReview required formType={formType} />

      <hr className="section-divider" />
      <h1 className="section-header">{i18n.t('history.pvq.employment.default.collection.caption')}</h1>
      <ConnectedEmployment
        {...subsectionProps}
        totalYears={requiredEmployment.requiredYears}
        recordYears={employmentRecordYears}
        realtime
        gaps={employmentGaps}
        formType={formType}
      />

      {requireHistoryFederalSection && <hr className="section-divider" />}
      {requireHistoryFederalSection && <FederalWrapper inReview formType={formType} />}
    </div>
  ) : (
    <div>
      {!forPrint && (
        <span>
          <ResidenceSummaryProgress
            years={residenceYears}
            dates={validResidenceDates}
            applicantBirthdate={applicantBirthdate}
          />

          <EmploymentSummaryProgress
            years={employmentYears}
            dates={validEmploymentDates}
            applicantBirthdate={applicantBirthdate}
            thresholdYears={thresholdYears}
          />

          <Show when={hasAttendedSchool || hasDegree}>
            <EducationSummaryProgress
              items={Education && Education.List && Education.List.items}
              errors={errors && errors[HISTORY_EDUCATION.key]}
            />
          </Show>
        </span>
      )}

      {!forPrint && <hr className="section-divider" />}

      <h1 className="section-header">{i18n.t('history.residence.collection.caption')}</h1>
      <ConnectedResidence
        {...subsectionProps}
        totalYears={requiredResidence.requiredYears}
        realtime
        gaps={residenceGaps}
      />

      <hr className="section-divider" />
      <h1 className="section-header">{i18n.t('history.employment.default.collection.caption')}</h1>
      <ConnectedEmployment
        {...subsectionProps}
        totalYears={requiredEmployment.requiredYears}
        recordYears={employmentRecordYears}
        realtime
        gaps={employmentGaps}
      />

      <hr className="section-divider" />
      <h1 className="section-header">{i18n.t('history.education.collection.caption')}</h1>
      <EducationWrapper inReview required />

      {requireHistoryFederalSection && <hr className="section-divider" />}
      {requireHistoryFederalSection && <FederalWrapper inReview />}
    </div>
  );
};

Review.propTypes = {
  applicantBirthdate: PropTypes.object,
  Education: PropTypes.object,
  Residence: PropTypes.object,
  Employment: PropTypes.object,
  formType: PropTypes.string,
  requireHistoryFederalSection: PropTypes.bool,
  forPrint: PropTypes.bool,
  errors: PropTypes.object,
};

Review.defaultProps = {
  applicantBirthdate: null,
  Education: { HasAttended: '', HasDegree10: '', List: { items: [] } },
  Residence: { List: { items: [] } },
  Employment: { List: { items: [] } },
  formType: null,
  requireHistoryFederalSection: true,
  forPrint: false,
  errors: {},
};

export default connectSubsection(Review, sectionConfig);
